import * as Immutable from "immutable";
import DiscussionModel from "./DiscussionModel";
import CommentModel from "./CommentModel";
import RatingModel from "./RatingModel";

export default class Modifiers {

    public static setAdmin(model: DiscussionModel, admin: boolean): DiscussionModel {
        return model.set("admin", admin) as DiscussionModel;
    }

    public static appendComment(model: DiscussionModel, comment: CommentModel): DiscussionModel {
        return model.update("comments", (comments) => {
            comment = comment.set("level", 0).set("first", true) as CommentModel;
            if (comments.count() > 0) {
                comments = comments.update(comments.first().getCommentId(), (first: CommentModel) => {
                    return first.set("first", false) as CommentModel;
                });
            } else {
                comment = comment.set("last", true) as CommentModel;
            }
            return Immutable.OrderedMap<number, CommentModel>(
                [[comment.getCommentId(), comment]],
            ).merge(comments);
        }) as DiscussionModel;
    }

    public static appendCommentAfter(model: DiscussionModel, comment: CommentModel, after: number): DiscussionModel {
        return model.update("comments", (map) => {

            let index = 0;
            map.forEach((value, key) => {
                if (key == after) {
                    return false;
                }
                index++;
            });

            let mapBefore = map.take(index + 1);
            let mapAfter = map.skip(index + 1);

            comment = comment.set("first", true).set("last", true) as CommentModel;

            if (mapBefore.count() > 0) {
                mapBefore = mapBefore.update(mapBefore.last().getCommentId(), (last: CommentModel) => {
                    comment = comment.set("level", last.getLevel() + 1) as CommentModel;
                    if (last.get("children") == false) {
                        last = last.set("children", true) as CommentModel;
                    }
                    return last;
                });
            }

            if (mapAfter.count() > 0) {
                mapAfter = mapAfter.update(mapAfter.first().getCommentId(), (first: CommentModel) => {
                    if (first.getLevel() < comment.getLevel()) {
                        if (first.get("previous") == false) {
                            first = first.set("previous", true) as CommentModel;
                        }
                    } else {
                        first = first.set("first", false) as CommentModel;
                        comment = comment.set("last", false) as CommentModel;
                    }
                    return first;
                });
            }

            return mapBefore.set(comment.getCommentId(), comment).merge(mapAfter);

        }) as DiscussionModel;
    }

    public static setAuthors(model: DiscussionModel, authors: Immutable.Set<number>): DiscussionModel {
        return model.set('authors', authors) as DiscussionModel;
    }

    public static setTotal(model: DiscussionModel, total: number): DiscussionModel {
        return model.set("total", total) as DiscussionModel;
    }

    public static setCommentRating(model: DiscussionModel, commentId: number, rating: number): DiscussionModel {
        return model.updateIn(["comments", commentId], (comment: CommentModel) => {
            if (comment) {
                return comment.set("rating", rating) as CommentModel;
            }
        }) as DiscussionModel;
    }

    public static updateCommentRating(model: DiscussionModel, commentId: number, delta: number): DiscussionModel {
        return model.updateIn(["comments", commentId], (comment: CommentModel) => {
            if (comment) {
                return comment.update("rating", (rating) => {
                    return rating + delta;
                }) as CommentModel;
            }
        }) as DiscussionModel;
    }

    public static setRating(model: DiscussionModel, commentId: number, weight: number): DiscussionModel {
        return model.updateIn(["ratings", commentId], (rating: RatingModel) => {
            if (rating) {
                return rating
                    .set("weight", weight)
                    .set("timestamp", Modifiers.getTimestamp()) as RatingModel;
            } else {
                return new RatingModel({
                    comment_id: commentId,
                    timestamp: Modifiers.getTimestamp(),
                    weight: weight,
                });
            }
        }) as DiscussionModel;
    }

    public static showDeleteModal(model: DiscussionModel, comment: number): DiscussionModel {
        return model
            .set("delete_modal", true)
            .set("delete_comment", comment) as DiscussionModel;
    }

    public static hideDeleteModal(model: DiscussionModel): DiscussionModel {
        return model
            .set("delete_modal", false)
            .set("delete_comment", 0) as DiscussionModel;
    }

    public static hideReportModal(model: DiscussionModel): DiscussionModel {
        return model
            .set("report_modal", false)
            .set("report_comment", 0) as DiscussionModel;
    }

    public static showReportModal(model: DiscussionModel, commentId: number): DiscussionModel {
        return model
            .set("report_modal", true)
            .set("report_comment", commentId) as DiscussionModel;
    }

    public static hideReportResultModal(model: DiscussionModel): DiscussionModel {
        return model.set("report_result_modal", false) as DiscussionModel;
    }

    public static showReportResultModal(model: DiscussionModel): DiscussionModel {
        return model.set("report_result_modal", true) as DiscussionModel;
    }

    public static hideBlockModal(model: DiscussionModel): DiscussionModel {
        return model
            .set("block_modal", false)
            .set("block_comment", 0) as DiscussionModel;
    }

    public static showBlockModal(model: DiscussionModel, commentId: number): DiscussionModel {
        return model
            .set("block_modal", true)
            .set("block_comment", commentId) as DiscussionModel;
    }

    public static expandComment(model: DiscussionModel, commentId: number): DiscussionModel {
        return model.updateIn(["comments", commentId], (comment: CommentModel) => {
            if (comment) {
                return comment.set("expanded", true) as CommentModel;
            }
        }) as DiscussionModel;
    }

    public static expandChildren(model: DiscussionModel, commentId: number): DiscussionModel {
        return model.update("expands", (map) => {
            return map.set(commentId, true);
        }) as DiscussionModel;
    }

    public static collapseChildren(model: DiscussionModel, commentId: number): DiscussionModel {
        return model.update("expands", (map) => {
            return map.set(commentId, false);
        }) as DiscussionModel;
    }

    public static blockComment(model: DiscussionModel, commentId: number): DiscussionModel {
        return model.updateIn(["comments", commentId], (comment: CommentModel) => {
            if (comment) {
                return comment.set("blocked", true) as CommentModel;
            }
        }) as DiscussionModel;
    }

    private static getTimestamp(): number {
        return Math.floor(Date.now() / 1000);
    }

    public static setFormSending(model: DiscussionModel, sending: boolean): DiscussionModel {
        return model.set('form_sending', sending) as DiscussionModel;
    }

    public static setReplySending(model: DiscussionModel, commentId: number, sending: boolean): DiscussionModel {
        return model.updateIn(["comments", commentId], (comment: CommentModel) => {
            if (comment) {
                return comment.set("reply_sending", sending);
            }
        }) as DiscussionModel;
    }

}